import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Refresh,
  Email,
  Edit,
  Delete,
  Timeline,
  BarChart,
} from '@mui/icons-material';
import { createInvite, getUserInvites, createCode, copyToClipboard, updateInvite, deleteInvite, updateCode, getInviteSignups, getInviteStats, getMyMargin } from './referralUtils';
import { formatInviteLink, formatDate, getTimeRemaining } from './referralUtils';
import { REFERRAL_CONFIG, MESSAGES, VALIDATION_RULES } from './referralConfig';

const InviteManagement = ({ isChampions = false }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);
  const [trackDialogOpen, setTrackDialogOpen] = useState(false);
  const [trackInvite, setTrackInvite] = useState(null);
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsInvite, setStatsInvite] = useState(null);
  const [inviteStats, setInviteStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [myMarginPercent, setMyMarginPercent] = useState(null);
  
  const [formData, setFormData] = useState({
    expires_in_days: REFERRAL_CONFIG.DEFAULT_INVITE_EXPIRY_DAYS,
    discount_type: '',
    discount_amount: '',
    total_usage: '',
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadInvites();
  }, []);

  useEffect(() => {
    if (isChampions) {
      getMyMargin().then((data) => setMyMarginPercent(data.margin_percent)).catch(() => setMyMarginPercent(null));
    }
  }, [isChampions]);

  const loadInvites = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserInvites();
      setInvites(data.invites || []);
      setIsAdmin(data.is_admin || false);
    } catch (err) {
      setError('Failed to load invites');
      console.error('Error loading invites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (isChampions && name === 'discount_amount' && value !== '') {
      const num = parseFloat(value);
      if (!isNaN(num) && num > 100) nextValue = '100';
    }
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (formData.expires_in_days < REFERRAL_CONFIG.MIN_INVITE_EXPIRY_DAYS || 
        formData.expires_in_days > REFERRAL_CONFIG.MAX_INVITE_EXPIRY_DAYS) {
      errors.expires_in_days = `Expiry must be between ${REFERRAL_CONFIG.MIN_INVITE_EXPIRY_DAYS} and ${REFERRAL_CONFIG.MAX_INVITE_EXPIRY_DAYS} days`;
    }
    if (isChampions) {
      const d = parseFloat(formData.discount_amount);
      if (formData.discount_amount === '' || isNaN(d) || d < 0 || d > 100) {
        errors.discount_amount = 'Discount % is required and must be between 0 and 100';
      }
    } else if (formData.discount_type) {
      if (!formData.discount_amount || formData.discount_amount <= 0) {
        errors.discount_amount = 'Discount amount is required and must be greater than 0';
      }
      if (formData.discount_type === 'PERCENT' && formData.discount_amount > 100) {
        errors.discount_amount = 'Discount percentage cannot exceed 100%';
      }
    }
    // Validate total usage if provided
    if (formData.total_usage && formData.total_usage < 1) {
      errors.total_usage = 'Total usage must be at least 1';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Generate a random code for the invite
  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      if (editingInvite) {
        const codeData = {
          discount_type: isChampions ? 'PERCENT' : (formData.discount_type || null),
          discount_amount: formData.discount_amount || null,
          usage_limit_total: formData.total_usage || null,
          ends_at: new Date(Date.now() + formData.expires_in_days * 24 * 60 * 60 * 1000).toISOString(),
        };
        
        await updateCode(editingInvite.code, codeData);
        
        const inviteData = {
          is_active: formData.is_active,
          expires_at: new Date(Date.now() + formData.expires_in_days * 24 * 60 * 60 * 1000).toISOString(),
        };
        
        const response = await updateInvite(editingInvite.id, inviteData);
        setInvites(prev => prev.map(invite => 
          invite.id === editingInvite.id ? response : invite
        ));
        setSnackbar({
          open: true,
          message: 'Invite updated successfully',
          severity: 'success'
        });
      } else {
        // Create new invite
        const codeData = {
          code: generateRandomCode(),
          code_type: 'REFERRAL',
          discount_type: isChampions ? 'PERCENT' : (formData.discount_type || null),
          discount_amount: formData.discount_amount || null,
          first_purchase_only: true,
          applies_to_roles: '0',
          applies_to_plans: '0',
          allow_stacking: false,
          auto_apply_if_cookie: true,
          per_user_limit: 1,
          usage_limit_total: formData.total_usage || null,
          starts_at: new Date().toISOString(),
          ends_at: new Date(Date.now() + formData.expires_in_days * 24 * 60 * 60 * 1000).toISOString(),
          is_active: true,
        };
        
        const codeResponse = await createCode(codeData);
        
        const inviteData = {
          email: '',
          expires_in_days: formData.expires_in_days,
          code_id: codeResponse.id,
        };
        
        const response = await createInvite(inviteData);
        setInvites(prev => [response.invite, ...prev]);
        setSnackbar({
          open: true,
          message: MESSAGES.SUCCESS.INVITE_CREATED,
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      resetForm();
    } catch (err) {
      setSnackbar({
        open: true,
        message: editingInvite ? 'Failed to update invite' : MESSAGES.ERROR.INVITE_CREATE_FAILED,
        severity: 'error'
      });
      console.error('Error with invite:', err);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInvite(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      expires_in_days: REFERRAL_CONFIG.DEFAULT_INVITE_EXPIRY_DAYS,
      discount_type: '',
      discount_amount: '',
      total_usage: '',
      is_active: true,
    });
    setFormErrors({});
  };

  const handleOpenTrack = async (invite) => {
    setTrackInvite(invite);
    setTrackDialogOpen(true);
    setSignups([]);
    setSignupsLoading(true);
    try {
      const data = await getInviteSignups(invite.id);
      setSignups(data.signups || []);
    } catch {
      setSignups([]);
    } finally {
      setSignupsLoading(false);
    }
  };

  const handleOpenStats = async (invite) => {
    setStatsInvite(invite);
    setInviteStats(null);
    setStatsDialogOpen(true);
    setStatsLoading(true);
    try {
      const data = await getInviteStats(invite.id);
      setInviteStats(data);
    } catch {
      setInviteStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleEditInvite = (invite) => {
    setEditingInvite(invite);
    setFormData({
      expires_in_days: invite.expires_at ? Math.ceil((new Date(invite.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : REFERRAL_CONFIG.DEFAULT_INVITE_EXPIRY_DAYS,
      discount_type: invite.code_details?.discount_type || '',
      discount_amount: invite.code_details?.discount_amount || '',
      total_usage: invite.code_details?.usage_limit_total || '',
      is_active: invite.is_active !== false,
    });
    setOpenDialog(true);
  };

  const handleDeleteInvite = (invite) => {
    setInviteToDelete(invite);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!inviteToDelete) return;
    
    try {
      await deleteInvite(inviteToDelete.id);
      setInvites(prev => prev.filter(invite => invite.id !== inviteToDelete.id));
      setSnackbar({
        open: true,
        message: 'Invite deleted successfully',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
      setInviteToDelete(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete invite',
        severity: 'error'
      });
      console.error('Error deleting invite:', err);
    }
  };

  const handleCopyLink = async (token) => {
    try {
      const link = formatInviteLink(token);
      await copyToClipboard(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };



  const handleShare = async (token) => {
    const link = formatInviteLink(token);
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on EduPlatform!',
          text: 'Use this invite link to get started:',
          url: link,
        });
      } catch (err) {
        console.error('Error sharing:', err);
        // Fallback to copying
        handleCopyLink(token);
      }
    } else {
      // Fallback to copying
      handleCopyLink(token);
    }
  };

  const getStatusChip = (invite) => {
    if (!invite.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    if (invite.accepted_at) {
      return <Chip label="Accepted" color="success" size="small" />;
    }
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return <Chip label="Expired" color="warning" size="small" />;
    }
    return <Chip label="Active" color="primary" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<Refresh />}
            onClick={loadInvites}
            variant="outlined"
          >
            Refresh
          </Button>
          <Button
            startIcon={<Add />}
            onClick={() => {
              setEditingInvite(null);
              resetForm();
              setOpenDialog(true);
            }}
            variant="contained"
          >
            Create Invite
          </Button>
        </Box>
      </Box>

      {/* Create/Edit Invite Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingInvite ? 'Edit Invite' : 'Create New Invite'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {editingInvite && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="is_active"
                      value={formData.is_active ? 'active' : 'deactivated'}
                      label="Status"
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="deactivated">Deactivated</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Deactivated links show &quot;Link expired&quot; when opened.
                    </Typography>
                  </FormControl>
                </Grid>
              )}
              {isChampions && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body1" color="text.secondary">
                        Your margin: {myMarginPercent != null ? `${myMarginPercent}%` : 'Not set by admin'}
                      </Typography>
                      <Typography variant="body1" color="primary" fontWeight="medium">
                        Updated margin: {myMarginPercent != null && formData.discount_amount !== '' && !isNaN(parseFloat(formData.discount_amount))
                          ? `${(myMarginPercent * (1 - Math.min(100, parseFloat(formData.discount_amount)) / 100)).toFixed(2)}%`
                          : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="discount_amount"
                      label="Discount (%)"
                      type="number"
                      value={formData.discount_amount}
                      onChange={handleInputChange}
                      placeholder="e.g. 25"
                      error={!!formErrors.discount_amount}
                      helperText={formErrors.discount_amount || 'Discount offered to the user (0-100, max 100)'}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      e.g. entitlement 30% and discount 50% → your margin becomes 15%
                    </Typography>
                  </Grid>
                </>
              )}
              {!isChampions && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        name="discount_type"
                        value={formData.discount_type}
                        onChange={handleInputChange}
                        label="Discount Type"
                        error={!!formErrors.discount_type}
                      >
                        <MenuItem value="">No Discount</MenuItem>
                        <MenuItem value="PERCENT">Percentage (%)</MenuItem>
                        <MenuItem value="FIXED">Fixed Amount</MenuItem>
                      </Select>
                    </FormControl>
                    {formErrors.discount_type && (
                      <Typography variant="caption" color="error">{formErrors.discount_type}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="discount_amount"
                      label="Discount Value"
                      type="number"
                      value={formData.discount_amount}
                      onChange={handleInputChange}
                      placeholder={formData.discount_type === 'PERCENT' ? '25' : '50'}
                      error={!!formErrors.discount_amount}
                      helperText={formErrors.discount_amount || (formData.discount_type === 'PERCENT' ? 'Enter percentage (0-100)' : 'Enter amount')}
                      inputProps={{
                        min: 0,
                        max: formData.discount_type === 'PERCENT' ? 100 : undefined,
                        step: formData.discount_type === 'PERCENT' ? 1 : 0.01,
                      }}
                      disabled={!formData.discount_type}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="total_usage"
                  label="Total Usage"
                  type="number"
                  value={formData.total_usage}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                  error={!!formErrors.total_usage}
                  helperText={formErrors.total_usage || 'Total number of times this invite can be used (leave empty for unlimited)'}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Expires In</InputLabel>
                  <Select
                    name="expires_in_days"
                    value={formData.expires_in_days}
                    onChange={handleInputChange}
                    label="Expires In"
                    error={!!formErrors.expires_in_days}
                  >
                    <MenuItem value={1}>1 day</MenuItem>
                    <MenuItem value={7}>7 days</MenuItem>
                    <MenuItem value={30}>30 days</MenuItem>
                    <MenuItem value={90}>90 days</MenuItem>
                    <MenuItem value={365}>1 year</MenuItem>
                  </Select>
                </FormControl>
                {formErrors.expires_in_days && (
                  <Typography variant="caption" color="error">
                    {formErrors.expires_in_days}
                  </Typography>
                )}
              </Grid>
              

            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingInvite ? 'Update Invite' : 'Create Invite'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadInvites}>
            Retry
          </Button>
        } sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Invites Table */}
      <Card>
        <CardContent>
                     <Typography variant="h6" gutterBottom>
             {isAdmin ? `All Invites (${invites.length})` : `Your Invites (${invites.length})`}
           </Typography>
          
          {invites.length === 0 ? (
            <Alert severity="info">
              {MESSAGES.INFO.NO_INVITES}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                                 <TableHead>
                   <TableRow>
                     <TableCell>Discount</TableCell>
                     <TableCell>Total Usage</TableCell>
                     <TableCell>Expires</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Created</TableCell>
                     <TableCell>Track</TableCell>
                     <TableCell>Statistic</TableCell>
                     {isAdmin && <TableCell>Owner</TableCell>}
                     <TableCell>Actions</TableCell>
                   </TableRow>
                 </TableHead>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                                             <TableCell>
                         {invite.code_details?.discount_amount ? (
                           <Typography variant="body2">
                             {invite.code_details.discount_type === 'PERCENT' ? `${invite.code_details.discount_amount}%` : `$${invite.code_details.discount_amount}`}
                           </Typography>
                         ) : (
                           <Typography variant="body2" color="text.secondary">
                             No discount
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {invite.code_details?.usage_limit_total || 'Unlimited'}
                         </Typography>
                       </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getTimeRemaining(invite.expires_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(invite)}
                      </TableCell>
                                             <TableCell>
                         <Typography variant="body2">
                           {formatDate(invite.created_at)}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Tooltip title="View signups and subscriptions">
                           <IconButton size="small" onClick={() => handleOpenTrack(invite)} color="primary">
                             <Timeline />
                           </IconButton>
                         </Tooltip>
                       </TableCell>
                       <TableCell>
                         <Tooltip title="View statistics">
                           <IconButton size="small" onClick={() => handleOpenStats(invite)} color="primary">
                             <BarChart />
                           </IconButton>
                         </Tooltip>
                       </TableCell>
                       {isAdmin && (
                         <TableCell>
                           <Typography variant="body2">
                             {invite.invited_by_email}
                           </Typography>
                         </TableCell>
                       )}
                       <TableCell>
                         <Box display="flex" gap={1}>
                           <Tooltip title="Copy invite link">
                             <IconButton
                               size="small"
                               onClick={() => handleCopyLink(invite.token)}
                               color="primary"
                             >
                               <ContentCopy />
                             </IconButton>
                           </Tooltip>
                           
                           <Tooltip title="Share invite">
                             <IconButton
                               size="small"
                               onClick={() => handleShare(invite.token)}
                               color="primary"
                             >
                               <Email />
                             </IconButton>
                           </Tooltip>

                           <Tooltip title="Edit invite">
                             <IconButton
                               size="small"
                               onClick={() => handleEditInvite(invite)}
                               color="secondary"
                             >
                               <Edit />
                             </IconButton>
                           </Tooltip>

                           <Tooltip title="Delete invite">
                             <IconButton
                               size="small"
                               onClick={() => handleDeleteInvite(invite)}
                               color="error"
                             >
                               <Delete />
                             </IconButton>
                           </Tooltip>
                         </Box>
                       </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Success Message */}
      {copied && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {MESSAGES.SUCCESS.COPIED_TO_CLIPBOARD}
        </Alert>
      )}

      {/* Track dialog: signups and subscriptions */}
      <Dialog open={trackDialogOpen} onClose={() => setTrackDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Signups for this invite</DialogTitle>
        <DialogContent>
          {trackInvite && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invite link: {formatInviteLink(trackInvite.token)}
            </Typography>
          )}
          {signupsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : signups.length === 0 ? (
            <Typography color="text.secondary">No signups yet.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First name</TableCell>
                    <TableCell>Last name</TableCell>
                    <TableCell>Subscription</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signups.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.first_name || row.name || '—'}</TableCell>
                      <TableCell>{row.last_name || '—'}</TableCell>
                      <TableCell>{row.subscription || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Statistic dialog: real metrics from backend */}
      <Dialog open={statsDialogOpen} onClose={() => setStatsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite statistics</DialogTitle>
        <DialogContent>
          {statsInvite && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invite: {formatInviteLink(statsInvite.token)}
            </Typography>
          )}
          {statsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : inviteStats ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Unique clicks (by IP)</Typography>
                <Typography variant="body2" fontWeight="medium">{inviteStats.unique_clicks ?? 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Registrations</Typography>
                <Typography variant="body2" fontWeight="medium">{inviteStats.registrations ?? 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Conversion rate</Typography>
                <Typography variant="body2" fontWeight="medium">{inviteStats.conversion_rate ?? 0}%</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Redemptions</Typography>
                <Typography variant="body2" fontWeight="medium">{inviteStats.redemptions ?? 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Discount value</Typography>
                <Typography variant="body2" fontWeight="medium">${Number(inviteStats.discount_value ?? 0).toFixed(2)}</Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">Could not load stats.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this invite? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InviteManagement;
